import React from 'react';

import { useTranslation } from '@/i18n';
import { SidePanelFormLayout } from '@/components/layout';
import { LabelInput, TextArea } from '@/components/ui/inputs';
import { SHIFT_COMMENT_MAX_LENGTH } from '@/api/domains/shift/shift.types';
import type { ShiftRequest } from '@/api/domains/shift/shift.types';


interface ShiftRequestDetailPanelProps {
  request: ShiftRequest;
}

/**
 * Panel lateral de solo lectura con el detalle de una solicitud de cambio de turno del
 * propio colaborador (ASIS-5554).
 *
 * Se alimenta de la fila ya cargada por el listado — no dispara una petición extra.
 * Los campos de resolución (fecha de revisión, aprobador y comentario del aprobador)
 * quedan con la indicación "Sin información" mientras la solicitud no ha sido revisada.
 * Ningún campo es editable ni hay acciones: la vista es exclusivamente de lectura.
 */
export const ShiftRequestDetailPanel: React.FC<ShiftRequestDetailPanelProps> = ({ request }) => {
  const { t: translate } = useTranslation();

  const noInformation = translate('attendance:shiftChange.detail.noInformation');
  const withFallback = (value: string): string => value || noInformation;

  return (
    <SidePanelFormLayout data-testid="shift-request-detail-panel">
      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:shiftChange.table.id')}
          value={String(request.id)}
          disabled
          readOnly
          data-testid="shift-request-detail-id"
        />
        <LabelInput
          label={translate('attendance:shiftChange.table.requestDate')}
          value={withFallback(request.requestDate)}
          disabled
          readOnly
          data-testid="shift-request-detail-request-date"
        />
        <LabelInput
          label={translate('attendance:shiftChange.table.dateFrom')}
          value={withFallback(request.dateFrom)}
          disabled
          readOnly
          data-testid="shift-request-detail-date-from"
        />
        <LabelInput
          label={translate('attendance:shiftChange.table.dateTo')}
          value={withFallback(request.dateTo)}
          disabled
          readOnly
          data-testid="shift-request-detail-date-to"
        />
        <LabelInput
          label={translate('attendance:shiftChange.detail.reviewDate')}
          value={withFallback(request.reviewDate)}
          disabled
          readOnly
          data-testid="shift-request-detail-review-date"
        />
        <LabelInput
          label={translate('attendance:shiftChange.detail.reviewerName')}
          value={withFallback(request.reviewerName)}
          disabled
          readOnly
          data-testid="shift-request-detail-reviewer-name"
        />
        <TextArea
          label={translate('attendance:shiftChange.detail.requesterComment')}
          value={withFallback(request.comments)}
          disabled
          readOnly
          maxLength={SHIFT_COMMENT_MAX_LENGTH}
          data-testid="shift-request-detail-requester-comment"
        />
        <TextArea
          label={translate('attendance:shiftChange.detail.approverComment')}
          value={withFallback(request.approverComment)}
          disabled
          readOnly
          maxLength={SHIFT_COMMENT_MAX_LENGTH}
          data-testid="shift-request-detail-approver-comment"
        />
      </SidePanelFormLayout.Fields>
    </SidePanelFormLayout>
  );
};

export default ShiftRequestDetailPanel;
